import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Building2, Sparkles, Crown, Star, Heart,
  Bed, Bath, Ruler, DollarSign, Filter, ArrowRight, Search,
  ChevronDown, X as XIcon, Car, Calendar, Layers, ShieldCheck,
  TrendingUp, Tag, Lock, CheckCircle2, Home as HomeIcon, ChevronLeft, ChevronRight
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { MASTER_PARTNERS, getPartnerBySlug } from "@/constants/masterPartners";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const PROPERTY_CATEGORIES = ["Venta", "Renta", "Comercial", "Industrial"];

const formatPrice = (n, period) => {
  const base = n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${(n / 1_000).toFixed(0)}K`;
  return period ? `${base} ${period}` : base;
};

const PillarMini = ({ label, value }) => (
  <div className="text-center">
    <div className="relative w-9 h-9 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="14" stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="none" />
        <circle cx="18" cy="18" r="14" stroke="#CCD500" strokeWidth="3" fill="none" strokeLinecap="round"
          strokeDasharray={`${(value / 100) * 2 * Math.PI * 14} ${2 * Math.PI * 14}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-display font-black text-white">{value}</div>
    </div>
    <p className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">{label}</p>
  </div>
);

const PropertyCard = ({ p, onView, onMatch, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: idx * 0.04 }}
    whileHover={{ y: -4 }}
    className="group rounded-2xl overflow-hidden bg-slate-900/60 backdrop-blur-md border border-white/10 hover:border-[#CCD500]/40 transition-all flex flex-col"
    data-testid={`partner-prop-${p.id}`}
  >
    <div className="relative h-52 overflow-hidden cursor-pointer" onClick={() => onView(p)}>
      <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
      <span className="absolute top-3 right-3 bg-[#CCD500] text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
        <Sparkles className="w-3.5 h-3.5" /> {p.match}%
      </span>
      <span className="absolute top-3 left-3 bg-slate-950/70 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10">
        {p.type}
      </span>
      {p.condition && (
        <span className="absolute top-12 left-3 bg-emerald-500/80 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          {p.condition}
        </span>
      )}
      <div className="absolute bottom-3 left-4 right-4 text-white">
        <h3 className="font-display text-lg font-bold leading-tight line-clamp-2">{p.title}</h3>
      </div>
    </div>
    <div className="p-4 flex flex-col flex-1">
      <div className="grid grid-cols-4 gap-1 text-center mb-3">
        <div className="flex items-center justify-center gap-1 text-xs text-slate-300">
          <Bed className="w-3.5 h-3.5 text-[#CCD500] shrink-0" /> <span>{p.bedrooms || "—"}</span>
        </div>
        <div className="flex items-center justify-center gap-1 text-xs text-slate-300">
          <Bath className="w-3.5 h-3.5 text-[#CCD500] shrink-0" /> <span>{p.bathrooms}</span>
        </div>
        <div className="flex items-center justify-center gap-1 text-xs text-slate-300">
          <Ruler className="w-3.5 h-3.5 text-[#CCD500] shrink-0" /> <span>{p.area}m²</span>
        </div>
        <div className="flex items-center justify-center gap-1 text-xs text-slate-300">
          <Car className="w-3.5 h-3.5 text-[#CCD500] shrink-0" /> <span>{p.parking || "—"}</span>
        </div>
      </div>
      {p.subtitle && (
        <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 mb-3">{p.subtitle}</p>
      )}
      {p.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {p.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
              {t}
            </span>
          ))}
        </div>
      )}
      {p.pillars && (
        <div className="grid grid-cols-4 gap-1 py-3 border-t border-white/5">
          <PillarMini label="Emoc." value={p.pillars.emotional} />
          <PillarMini label="Téc." value={p.pillars.technical} />
          <PillarMini label="Urb." value={p.pillars.urban} />
          <PillarMini label="Fin." value={p.pillars.financial} />
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-white/5 gap-2 mt-auto">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 truncate">
            {p.category === "Renta" ? "Renta mensual" : "Precio"} · ${p.pricePerM2?.toLocaleString() || "—"}/m²
          </p>
          <p className="font-display text-lg font-black text-[#CCD500] truncate">{formatPrice(p.price, p.rentalPeriod)}</p>
        </div>
        <Button
          onClick={(e) => { e.stopPropagation(); onView(p); }}
          data-testid={`partner-prop-view-${p.id}`}
          size="sm"
          className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 rounded-full px-4 h-9 shrink-0"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          Ver ficha
        </Button>
      </div>
    </div>
  </motion.div>
);

const PILLAR_LABELS = {
  emotional: { label: "Emocional", desc: "Vibra del espacio, luz, energía y conexión personal", color: "from-rose-500/20 to-rose-500/0", ring: "stroke-rose-400" },
  technical: { label: "Técnico", desc: "Calidad constructiva, acabados, instalaciones", color: "from-blue-500/20 to-blue-500/0", ring: "stroke-blue-400" },
  urban: { label: "Urbano", desc: "Ubicación, servicios, conectividad y entorno", color: "from-violet-500/20 to-violet-500/0", ring: "stroke-violet-400" },
  financial: { label: "Financiero", desc: "Valor, plusvalía proyectada y rentabilidad", color: "from-emerald-500/20 to-emerald-500/0", ring: "stroke-emerald-400" },
};

const PillarFull = ({ k, value }) => {
  const meta = PILLAR_LABELS[k] || PILLAR_LABELS.emotional;
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${meta.color} border border-white/10 p-3 sm:p-4`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{meta.label}</p>
          <p className="font-display text-2xl font-black text-white leading-none">{value}<span className="text-sm text-slate-400">/100</span></p>
        </div>
        <div className="relative w-12 h-12 shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
            <circle cx="18" cy="18" r="14" className={meta.ring} strokeWidth="4" fill="none" strokeLinecap="round"
              strokeDasharray={`${(value / 100) * 2 * Math.PI * 14} ${2 * Math.PI * 14}`} />
          </svg>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-2 leading-snug">{meta.desc}</p>
    </div>
  );
};

const InfoStat = ({ icon: Icon, value, label }) => (
  <div className="p-2.5 sm:p-3 rounded-xl bg-slate-900/60 border border-white/10 text-center">
    <Icon className="w-4 h-4 text-[#CCD500] mx-auto mb-1.5" />
    <p className="font-display text-base sm:text-lg font-bold text-white leading-none">{value}</p>
    <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1 leading-tight">{label}</p>
  </div>
);

// Build a sensible mini-gallery from the property image (rotate through pool to differ)
const PROP_GALLERY_POOL = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=85",
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=900&q=85",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&q=85",
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=900&q=85",
];

const PropertyDetailModal = ({ property, partner, open, onClose, navigate }) => {
  const [galleryIdx, setGalleryIdx] = useState(0);
  useEffect(() => { setGalleryIdx(0); }, [property?.id]);

  if (!property) return null;

  // Build gallery: main image + 3 staged from pool
  const gallery = [property.img, ...PROP_GALLERY_POOL.filter((g) => g !== property.img).slice(0, 3)];

  // Light mortgage estimate (20% enganche, 20 años, 10.5% anual) — for ventas
  const isRent = property.category === "Renta";
  const enganche = Math.round(property.price * 0.2);
  const monthly = (() => {
    if (isRent) return null;
    const principal = property.price - enganche;
    const r = 0.105 / 12;
    const n = 20 * 12;
    const m = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(m);
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl w-[calc(100vw-1rem)] sm:w-auto bg-[#0B1120] border border-white/10 text-slate-100 max-h-[92vh] overflow-y-auto overflow-x-hidden p-0 sm:p-0 gap-0"
        data-testid="partner-property-modal"
      >
        <VisuallyHidden>
          <DialogTitle>{property.title}</DialogTitle>
          <DialogDescription>Ficha de propiedad operada por {partner.mp} en {partner.city}.</DialogDescription>
        </VisuallyHidden>
        {/* Gallery Hero */}
        <div className="relative h-56 sm:h-72 md:h-80 bg-slate-950 w-full overflow-hidden">
          <img src={gallery[galleryIdx]} alt={property.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/30 to-transparent" />

          {/* Gallery prev/next */}
          {gallery.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx - 1 + gallery.length) % gallery.length); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                aria-label="Anterior"
                data-testid="modal-gallery-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx + 1) % gallery.length); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                aria-label="Siguiente"
                data-testid="modal-gallery-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setGalleryIdx(i); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === galleryIdx ? "bg-[#CCD500] w-5" : "bg-white/40 hover:bg-white/70"}`}
                    aria-label={`Foto ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Top-right badges */}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end max-w-[40%]">
            <span className="bg-[#CCD500] text-black px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold flex items-center gap-1 shadow-lg whitespace-nowrap">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {property.match}%
            </span>
            <span className="bg-slate-950/70 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border border-white/10 whitespace-nowrap">
              {property.type}
            </span>
            {property.condition && (
              <span className="bg-emerald-500/80 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full whitespace-nowrap">
                {property.condition}
              </span>
            )}
          </div>

          {/* Title overlay */}
          <div className="absolute bottom-3 left-3 right-3 sm:left-5 sm:right-5 text-white min-w-0">
            <p className="text-[11px] text-slate-300 uppercase tracking-wider flex items-center gap-1.5 truncate">
              <MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{partner.city} · Operado por {partner.mp}</span>
            </p>
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-black leading-tight mt-1 line-clamp-2 break-words">{property.title}</h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 sm:p-5 md:p-6 space-y-4 sm:space-y-5 w-full min-w-0">
          {/* Price block */}
          <div className="rounded-2xl bg-gradient-to-br from-[#CCD500]/15 to-transparent border border-[#CCD500]/30 p-3 sm:p-5">
            <div className="flex flex-wrap items-end justify-between gap-3 min-w-0">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-slate-400">
                  {isRent ? "Renta mensual" : "Precio de venta"}
                </p>
                <p className="font-display text-2xl sm:text-3xl md:text-4xl font-black text-[#CCD500] leading-none break-all">
                  {formatPrice(property.price, property.rentalPeriod)}
                </p>
                <p className="text-[11px] sm:text-xs text-slate-400 mt-1">
                  ${property.pricePerM2?.toLocaleString() || "—"} MXN/m² · {property.area} m² totales
                </p>
              </div>
              {!isRent && monthly && (
                <div className="text-right border-l border-white/10 pl-3 sm:pl-4 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">Mensualidad est.</p>
                  <p className="font-display text-base sm:text-xl font-bold text-white break-all">{formatPrice(monthly)} <span className="text-[10px] sm:text-xs text-slate-400">/mes</span></p>
                  <p className="text-[10px] text-slate-500 mt-0.5">20% eng · 20a · 10.5%</p>
                </div>
              )}
            </div>
          </div>

          {/* Subtitle / Description */}
          {property.subtitle && (
            <div>
              <p className="text-sm text-slate-300 leading-relaxed">{property.subtitle}</p>
            </div>
          )}

          {/* Key facts grid */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
            {property.bedrooms > 0 && <InfoStat icon={Bed} value={property.bedrooms} label="Recám." />}
            {property.bathrooms > 0 && <InfoStat icon={Bath} value={property.bathrooms} label="Baños" />}
            <InfoStat icon={Ruler} value={`${property.area}m²`} label="Constr." />
            {property.parking != null && <InfoStat icon={Car} value={property.parking} label="Estac." />}
            {property.levels != null && <InfoStat icon={Layers} value={property.levels} label="Niveles" />}
            {property.yearBuilt && <InfoStat icon={Calendar} value={property.yearBuilt} label="Año" />}
          </div>

          {/* Tags */}
          {property.tags?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#CCD500] font-bold mb-2 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Características
              </p>
              <div className="flex flex-wrap gap-2">
                {property.tags.map((t) => (
                  <span key={t} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-200">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Amenities */}
          {property.amenities?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#CCD500] font-bold mb-2 flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5" /> Amenidades
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {property.amenities.map((a, i) => (
                  <div key={`amen-${i}-${a}`} className="flex items-center gap-2 text-sm text-slate-300 min-w-0">
                    <CheckCircle2 className="w-4 h-4 text-[#CCD500] shrink-0" />
                    <span className="break-words min-w-0">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nearby / Entorno */}
          {property.nearby?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#CCD500] font-bold mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Entorno cercano
              </p>
              <div className="space-y-2">
                {property.nearby.map((n, i) => (
                  <div key={`near-${i}-${n}`} className="flex items-start gap-2 text-sm text-slate-300 min-w-0">
                    <ArrowRight className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" />
                    <span className="break-words min-w-0">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 4 Pillar Match Breakdown */}
          {property.pillars && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#CCD500] font-bold mb-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Score psicográfico · 52 puntos analizados
              </p>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {Object.entries(property.pillars).map(([k, v]) => (
                  <PillarFull key={k} k={k} value={v} />
                ))}
              </div>
            </div>
          )}

          {/* Investment hint for non-renta */}
          {!isRent && property.category !== "Industrial" && (
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-900/60 border border-white/10 flex items-start gap-3 min-w-0">
              <TrendingUp className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">Plusvalía proyectada</p>
                <p className="text-xs text-slate-400 mt-1 break-words">
                  En zonas similares de {partner.city}, propiedades como esta han mostrado una apreciación promedio de 6-9% anual en los últimos 36 meses.
                </p>
              </div>
            </div>
          )}

          {/* Master Partner badge */}
          <div className="p-3 sm:p-4 rounded-2xl bg-slate-900/60 border border-white/10 flex items-start gap-3 min-w-0">
            <Crown className="w-5 h-5 text-[#CCD500] shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-white truncate">{partner.mp}</p>
              <p className="text-xs text-slate-400 mt-0.5 break-words">
                Master Partner certificado en {partner.city}. Coordina la operación y validación legal con agentes profesionales.
              </p>
            </div>
            <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5 hidden sm:block" />
          </div>

          {/* Locked extras CTA */}
          <div className="rounded-2xl bg-gradient-to-br from-[#CCD500]/15 to-fuchsia-500/5 border border-[#CCD500]/30 p-3 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-[#CCD500]" />
              <p className="text-[11px] uppercase tracking-widest text-[#CCD500] font-bold">Desbloquea con tu cuenta gratis</p>
            </div>
            <ul className="space-y-2 text-sm text-slate-200 mb-4">
              <li className="flex items-start gap-2 min-w-0"><CheckCircle2 className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" /><span className="min-w-0 break-words">Galería completa con 12+ fotos y planos arquitectónicos.</span></li>
              <li className="flex items-start gap-2 min-w-0"><CheckCircle2 className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" /><span className="min-w-0 break-words">Ubicación exacta, colonia y mapa interactivo.</span></li>
              <li className="flex items-start gap-2 min-w-0"><CheckCircle2 className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" /><span className="min-w-0 break-words">Contacto directo con agente certificado por el Master Partner.</span></li>
              <li className="flex items-start gap-2 min-w-0"><CheckCircle2 className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" /><span className="min-w-0 break-words">Match psicográfico personalizado (no genérico) con tu perfil.</span></li>
              <li className="flex items-start gap-2 min-w-0"><CheckCircle2 className="w-4 h-4 text-[#CCD500] mt-0.5 shrink-0" /><span className="min-w-0 break-words">Agenda visita virtual o presencial con un click.</span></li>
            </ul>
            <Button
              onClick={() => navigate("/comprador")}
              data-testid="partner-prop-register-btn"
              className="w-full bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] rounded-full h-12"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Crear cuenta gratis para hacer match
            </Button>
            <p className="text-[11px] text-slate-500 text-center mt-2">100% gratis para compradores · Sin tarjeta · Sin compromisos</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MasterPartnerPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const partner = getPartnerBySlug(slug);
  const [activeCategory, setActiveCategory] = useState("Venta");
  const [selectedProp, setSelectedProp] = useState(null);

  useEffect(() => {
    if (!partner) navigate("/", { replace: true });
  }, [partner, navigate]);

  if (!partner) return null;

  const filtered = partner.properties.filter((p) => (p.category || "Venta") === activeCategory);
  const categoryCounts = PROPERTY_CATEGORIES.reduce((acc, c) => {
    acc[c] = partner.properties.filter((p) => (p.category || "Venta") === c).length;
    return acc;
  }, {});
  const otherPartners = MASTER_PARTNERS.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body selection:bg-[#CCD500] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors shrink-0"
            data-testid="partner-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Volver al inicio</span>
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="h-7 sm:h-8 brightness-0 invert"
          />
          {user ? (
            <Button
              onClick={() => navigate("/swipe")}
              className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-3 sm:px-5 text-xs sm:text-sm shrink-0"
              data-testid="partner-dashboard-btn"
            >
              Mi panel
            </Button>
          ) : (
            <Button
              onClick={() => navigate("/auth")}
              className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-3 sm:px-5 text-xs sm:text-sm shrink-0"
              data-testid="partner-cta-btn"
            >
              Empezar
            </Button>
          )}
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className="pt-20 px-4 sm:px-6 lg:px-8 bg-[#0B1120]">
        <div className="max-w-7xl mx-auto py-3">
          <Breadcrumbs items={[
            { label: "Haz Match con tu casa ideal", to: "/haz-match" },
            { label: partner.city }
          ]} />
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative h-[380px] sm:h-[420px] md:h-[520px]">
          <img src={partner.img} alt={partner.city} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/70 to-[#0B1120]/30" />
          <div className={`absolute inset-0 bg-gradient-to-br ${partner.color} opacity-20 mix-blend-overlay`} />
          <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#CCD500]/10 rounded-full blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-10 sm:pb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl w-full"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#CCD500] text-black mb-4 sm:mb-5 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
                <Crown className="w-3.5 h-3.5" />
                Master Partner · {partner.region}
              </div>
              <h1 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-[0.95] break-words" data-testid="partner-name">
                {partner.mp}
              </h1>
              <p className="mt-3 sm:mt-4 text-base sm:text-xl md:text-2xl text-slate-200 max-w-2xl">
                {partner.tagline}
              </p>
              <div className="mt-5 sm:mt-6 flex flex-wrap gap-x-3 gap-y-2 items-center">
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-slate-200">
                  <MapPin className="w-4 h-4 text-[#CCD500]" /> {partner.city}
                </span>
                <span className="text-slate-500 hidden sm:inline">·</span>
                <span className="inline-flex items-center gap-2 text-xs sm:text-sm text-slate-200">
                  <Building2 className="w-4 h-4 text-[#CCD500]" /> {partner.props} propiedades
                </span>
                <span className="text-slate-500 hidden sm:inline">·</span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-white/10 text-white border border-white/20">
                  {partner.tag}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About + Stats */}
      <section className="relative py-10 sm:py-12 px-4 sm:px-6 lg:px-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
              <span className="h-px w-8 bg-[#CCD500]/60" /> Sobre {partner.mp}
            </span>
            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="partner-description">
              {partner.description}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: partner.props, l: "Propiedades activas" },
              { v: `${Math.round(partner.properties.reduce((s, p) => s + p.match, 0) / partner.properties.length)}%`, l: "Match promedio" },
              { v: filtered.length > 0 ? formatPrice(Math.min(...partner.properties.map((p) => p.price))) : "—", l: "Desde" },
              { v: filtered.length > 0 ? formatPrice(Math.max(...partner.properties.map((p) => p.price))) : "—", l: "Hasta" },
            ].map((s) => (
              <div key={s.l} className="p-3 sm:p-4 rounded-2xl bg-slate-900/60 border border-white/10">
                <p className="font-display text-xl sm:text-2xl md:text-3xl font-black text-white break-all">{s.v}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1 leading-tight">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Properties Listing */}
      <section className="relative py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-6 mb-6 sm:mb-8">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
                <span className="h-px w-8 bg-[#CCD500]/60" /> Portafolio Curado
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mt-3 tracking-tight">
                Propiedades destacadas
              </h2>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
              <Filter className="w-4 h-4 text-slate-500 shrink-0" />
              {PROPERTY_CATEGORIES.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveCategory(t)}
                  data-testid={`partner-category-${t.toLowerCase()}`}
                  className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all border flex items-center gap-2 ${
                    activeCategory === t
                      ? "bg-[#CCD500] text-black border-[#CCD500]"
                      : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {t}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                    activeCategory === t ? "bg-black/10 text-black" : "bg-white/10 text-slate-400"
                  }`}>
                    {categoryCounts[t] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 rounded-2xl bg-slate-900/40 border border-white/10">
              <Search className="w-10 h-10 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No hay propiedades de este tipo en el portafolio.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, i) => (
                <PropertyCard
                  key={p.id}
                  p={p}
                  idx={i}
                  onView={setSelectedProp}
                  onMatch={() => navigate("/comprador")}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Master Partners */}
      <section className="py-16 px-6 lg:px-8 border-t border-white/5 bg-slate-950/40">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
                <span className="h-px w-8 bg-[#CCD500]/60" /> Red Findperty
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 tracking-tight">
                Otros Master Partners
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {otherPartners.map((p) => (
              <button
                key={p.slug}
                onClick={() => {
                  navigate(`/partner/${p.slug}`);
                  window.scrollTo({ top: 0, behavior: "instant" });
                }}
                data-testid={`partner-other-${p.slug}`}
                className="group text-left rounded-2xl overflow-hidden bg-slate-900/60 border border-white/10 hover:border-[#CCD500]/40 transition-all"
              >
                <div className="relative h-32 overflow-hidden">
                  <img src={p.img} alt={p.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-300">{p.city}</p>
                    <p className="font-display text-base font-bold text-white truncate">{p.mp}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <Crown className="w-10 h-10 text-black mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-5xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              ¿Te interesa una propiedad de {partner.mp}?
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-7">
              Regístrate gratis en Findperty para ver detalles, hacer match psicográfico y ser contactado por el agente que opera tu propiedad ideal.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/comprador")}
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base"
              data-testid="partner-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Crear cuenta gratis
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="h-8 brightness-0 invert opacity-60"
          />
          <p className="text-sm text-slate-500">© 2026 Findperty · Master Partner {partner.region}</p>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Ver todos los Master Partners →
          </button>
        </div>
      </footer>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        property={selectedProp}
        partner={partner}
        open={!!selectedProp}
        onClose={() => setSelectedProp(null)}
        navigate={navigate}
      />
    </div>
  );
};

export default MasterPartnerPage;
